import { put, call, spawn } from 'redux-saga/effects'
import { chargingTokenService, handleError } from 'Services/ApiService.js'
import CardActions from 'Stores/Card/Actions'
import history from 'history/browser'
import { encodeObjectToBase64 } from 'Utils/base64functions'
import { IFToastMessage } from 'Components'
import { toaster } from 'rsuite'

export function* fetchCardListCount({ filter }) {
  yield put(CardActions.fetchCardListCountLoading())
  try {
    if (filter != null) {
      filter = encodeObjectToBase64(filter)
    }
    const { data } = yield call(chargingTokenService.get, '/count', {
      params: {
        filter: filter,
      },
    })
    yield put(CardActions.fetchCardListCountSuccess(data.tokensCount))
  } catch (e) {
    yield put(CardActions.fetchCardListCountFail(e))
    if (e.message !== 'Network Error') handleError(e)
  }
}
export function* fetchCardList({ filter, offset, onNotFound }) {
  if (offset === 0) {
    yield spawn(fetchCardListCount, { filter })
    yield put(CardActions.fetchCardListLoading())
  }
  try {
    if (filter != null) {
      filter = encodeObjectToBase64(filter)
    }

    const { data } = yield call(chargingTokenService.get, '/', {
      params: {
        filter: filter,
        offset: offset,
      },
    })
    const slashArray = history.location.pathname.split('/')
    const cardIdParam = slashArray[2]

    if (cardIdParam && cardIdParam.match(/^[0-9a-fA-F]{24}$/) && offset === 0) {
      const index = data.chargingTokens.findIndex(
        (token) => token._id === cardIdParam,
      )
      if (index !== -1) {
        yield put(
          CardActions.fetchCardListSuccess(
            offset,
            data.chargingTokens,
            data.nextOffset,
            null,
            index,
          ),
        )
      } else {
        const fetchedCard = yield call(
          chargingTokenService.get,
          `/${cardIdParam}`,
        )

        if (!fetchedCard.data) {
          onNotFound()
          return
        }
        yield put(
          CardActions.fetchCardListSuccess(
            offset,
            data.chargingTokens,
            data.nextOffset,
            fetchedCard.data.chargingToken,
            data.chargingTokens.length - 1,
          ),
        )
      }
    } else {
      yield put(
        CardActions.fetchCardListSuccess(
          offset,
          data.chargingTokens,
          data.nextOffset,
          null,
          0,
        ),
      )
    }
  } catch (e) {
    yield put(CardActions.fetchCardListFail(e))
    handleError(e)
    e = yield e
    if (e.status === 404) onNotFound()
  }
}

export function* deleteCard({ cardId, ref }) {
  yield put(CardActions.deleteCardLoading())
  try {
    const { data } = yield call(chargingTokenService.delete, `/${cardId}`, {})
    yield put(CardActions.deleteCardSuccess(cardId))
    if (ref) ref.current.closeAccordion()
    toaster.push(<IFToastMessage type="success" text={'Card deleted'} />, {
      placement: 'topEnd',
    })
  } catch (e) {
    yield put(CardActions.deleteCardFail(e))
    handleError(e)
  }
}

export function* updateCard({ cardId, newCard, index }) {
  yield put(CardActions.updateCardLoading())
  try {
    yield call(chargingTokenService.patch, `/${cardId}`, {
      ...newCard,
    })

    yield put(CardActions.updateCardSuccess(index, newCard))
  } catch (e) {
    yield put(CardActions.updateCardFail(e))
    handleError(e)
  }
}

export function* addCard({ newCard }) {
  yield put(CardActions.addCardLoading())
  try {
    const { data } = yield call(chargingTokenService.post, `/`, {
      ...newCard,
    })
    yield put(CardActions.addCardSuccess(data))
  } catch (e) {
    yield put(CardActions.addCardFail(e))
    handleError(e)
  }
}

export function* addCardBulk({ file }) {
  yield put(CardActions.addCardLoading())
  try {
    const { data } = yield call(
      chargingTokenService.post,
      `/chargingTokenBulk`,
      file,
    )
    yield put(CardActions.addCardBulkSuccess(data))
  } catch (e) {
    yield put(CardActions.addCardFail(e))
    handleError(e)
  }
}

export function* fetchCardTransactions({ filter, offset, cardId, ascending }) {
  if (offset === 0) yield put(CardActions.fetchCardTransactionsLoading())
  else yield put(CardActions.fetchCardTransactionsLoadingMore())
  try {
    if (filter !== null) {
      filter = encodeObjectToBase64(filter)
    }
    if (cardId) {
      const { data } = yield call(
        chargingTokenService.get,
        `/${cardId}/transactions`,
        {
          params: {
            filter: filter,
            offset: offset,
            ascending: ascending,
          },
        },
      )
      yield put(
        CardActions.fetchCardTransactionsSuccess(
          offset,
          data.transactions,
          data.nextOffset,
        ),
      )
    }
  } catch (e) {
    yield put(CardActions.fetchCardTransactionsFail(e))
    handleError(e)
  }
}

export function* fetchCard({ cardId }) {
  yield put(CardActions.fetchCardLoading())
  try {
    const { data } = yield call(chargingTokenService.get, `/${cardId}/`)
    yield put(CardActions.fetchCardSuccess(data))
  } catch (e) {
    yield put(CardActions.fetchCardFail(e))
    handleError(e)
  }
}

export function* addCardAccessGroups({ cardId, accessGroups }) {
  yield put(CardActions.addCardAccessGroupsLoading())
  try {
    const { data } = yield call(
      chargingTokenService.post,
      `/${cardId}/accessGroups/bulk`,
      {
        accessGroups: accessGroups,
      },
    )
    yield put(CardActions.addCardAccessGroupsSuccess(data.accessGroups))
  } catch (e) {
    yield put(CardActions.addCardAccessGroupsFail(e))
    handleError(e)
  }
}

export function* deleteCardAccessGroups({ cardId, accessGroups }) {
  yield put(CardActions.deleteCardAccessGroupsLoading())
  try {
    accessGroups = accessGroups.map((group) => {
      return group.id
    })
    const { data } = yield call(
      chargingTokenService.delete,
      `/${cardId}/accessGroups/bulk`,
      {
        data: { accessGroups },
      },
    )

    yield put(CardActions.deleteCardAccessGroupsSuccess(accessGroups))
  } catch (e) {
    yield put(CardActions.deleteCardAccessGroupsFail(e))
    handleError(e)
  }
}

export function* fetchLocalListFilteredCards({ filter, offset }) {
  yield put(CardActions.fetchLocalListFilteredCardsLoading())

  try {
    if (filter != null) {
      filter = encodeObjectToBase64(filter)
    }

    const { data } = yield call(chargingTokenService.get, '/', {
      params: {
        filter: filter,
        offset: offset,
      },
    })

    yield put(
      CardActions.fetchLocalListFilteredCardsSuccess(
        offset,
        data.chargingTokens,
        data.nextOffset,
      ),
    )
  } catch (e) {
    yield put(CardActions.fetchLocalListFilteredCardsFail(e))
    handleError(e)
  }
}

export function* fetchCardSessionList({ cardId }) {
  yield put(CardActions.fetchSessionListCardLoading())
  try {
    const { data } = yield call(
      chargingTokenService.get,
      `/${cardId}/chargingSessions`,
    )
    yield put(CardActions.fetchSessionListCardSuccess(data.chargingSessions))
  } catch (e) {
    yield put(CardActions.fetchSessionListCardFail(e))
    if (e.message !== 'Network Error') handleError(e)
  }
}

export function* fetchCardTransactionStats({ cardId, from, to, timezone }) {
  yield put(CardActions.fetchCardTransactionStatsLoading())
  try {
    const { data } = yield call(
      chargingTokenService.get,
      `/${cardId}/transactionStats`,
      {
        params: {
          from: from,
          to: to,
          timezone: timezone,
        },
      },
    )
    yield put(CardActions.fetchCardTransactionStatsSuccess(data))
  } catch (e) {
    yield put(CardActions.fetchCardTransactionStatsFail(e))
    if (e.message !== 'Network Error') handleError(e)
  }
}
